import { batchStatusMap } from '@/components/BatchList';
import SridSelector from '@/components/SridSelector';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { execute } from '@/graphql/execute';
import { Attachment, ProcessJobType, UploadBatchStatus } from '@/graphql/graphql';
import { startBatchProcessing } from '@/queries/attachment.query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { ChevronDownIcon } from 'lucide-react';
import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

const getDPI = () => {
  const div = document.createElement('div');
  div.style.height = '1in';
  div.style.width = '1in';
  div.style.top = '-100%';
  div.style.left = '-100%';
  div.style.position = 'absolute';

  document.body.appendChild(div);

  const result = div.offsetHeight;

  document.body.removeChild(div);

  return result;
};

const mapboxResolution = (zoom: number, latitude = 0) => {
  const earthCircumference = 40075016.686; // meters
  const tileSize = 256; // pixels per tile
  return (earthCircumference * Math.cos((latitude * Math.PI) / 180)) / (tileSize * Math.pow(2, zoom));
};

const mapboxScale = (zoom: number, latitude = 0) => {
  const metersPerPixel = mapboxResolution(zoom, latitude);
  return (metersPerPixel * getDPI()) / 0.0254; // unitless ratio (1 : scale)
};

const niceRound = (value: number) => {
  if (value === 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / Math.pow(10, exponent);
  let niceFraction;
  if (fraction < 1.5) niceFraction = 1;
  else if (fraction < 3) niceFraction = 2;
  else if (fraction < 7) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * Math.pow(10, exponent);
};

interface iHeaderProps {
  status: UploadBatchStatus;
  srid: number;
  attachments: Attachment[];
}

const dxfOutputVersions = {
  ACAD9: 'ACAD9',
  ACAD10: 'ACAD10',
  ACAD12: 'ACAD12',
  ACAD13: 'ACAD13',
  ACAD14: 'ACAD14',
  ACAD2000: 'ACAD2000',
  ACAD2004: 'ACAD2004',
  ACAD2007: 'ACAD2007',
  ACAD2010: 'ACAD2010',
  ACAD2013: 'ACAD2013',
  ACAD2018: 'ACAD2018',
} as const;

const pointCloudAttributes = {
  rgb: 'RGB',
  intensity: 'Intensity',
  classification: 'Classification',
  'return number': 'Return Number',
  // todo: pnrext three loader does not support this yet
  // 'number of returns': 'Number of Returns',
  'point source id': 'Source ID',
} as const;

const pcAttributeKeys: Record<string, string> = {
  rgb: 'rgb',
  intensity: 'intensity',
  classification: 'classification',
  'return number': 'returnNumber',
  'point source id': 'sourceId',
};

const processTypes: Record<ProcessJobType, string> = {
  E57: 'E57',
  LAS: 'LAS/LAZ',
  PTS: 'PTS/XYZ/TXT',
  POST: 'POST',
  IMAGE: 'Image',
  IPRJ: 'IPRJ',
  GEOJSON: 'GeoJSON',
  DWG: 'DWG',
  DXF: 'DXF',
  SHP: 'SHP',
  IFC: 'IFC',
  GEOTIFF: 'GeoTIFF',
  IMAGE_COMPRESSION: 'Image compression',
  RCS: 'RCS',
  LANDXML: 'LandXML',
  XGRIDS: 'XGRIDS',
  FJD_IMAGE: 'FJD Image',
  WMS: 'WMS',
  POTREE: 'Potree',
} as const;

const getTypesFromFiles = (files: Attachment[]): string[] => {
  const fileTypes = files.map((file) => file.filename.split('.').pop());
  const supportedTypes = [];
  if (fileTypes.includes('dwg')) supportedTypes.push('DWG');
  if (fileTypes.includes('dxf')) supportedTypes.push('DXF');
  if (fileTypes.includes('shp')) supportedTypes.push('SHP');
  if (fileTypes.includes('e57')) supportedTypes.push('E57');
  if (fileTypes.includes('las')) supportedTypes.push('LAS', 'GEOTIFF');
  if (fileTypes.includes('laz')) supportedTypes.push('LAS');
  if (fileTypes.includes('pts')) supportedTypes.push('PTS');
  if (fileTypes.includes('xyz')) supportedTypes.push('PTS');
  if (fileTypes.includes('txt')) supportedTypes.push('PTS');
  if (fileTypes.includes('post')) supportedTypes.push('POST');
  if (fileTypes.includes('cal') && fileTypes.includes('lst') && fileTypes.includes('iprj')) supportedTypes.push('IPRJ');
  if (fileTypes.includes('geojson')) supportedTypes.push('GEOJSON');
  if (fileTypes.includes('json')) supportedTypes.push('GEOJSON');
  if (fileTypes.includes('ifc')) supportedTypes.push('IFC');
  if (fileTypes.includes('xml')) supportedTypes.push('LANDXML');
  if (fileTypes.includes('lcc')) supportedTypes.push('XGRIDS');
  if (fileTypes.includes('tif')) supportedTypes.push('GEOTIFF');
  if (fileTypes.includes('tiff')) supportedTypes.push('GEOTIFF');
  if (fileTypes.includes('rcs')) supportedTypes.push('RCS');
  if (fileTypes.includes('csv') && fileTypes.includes('jpg')) supportedTypes.push('FJD_IMAGE');
  return [...new Set(supportedTypes)];
};

const Header: FunctionComponent<iHeaderProps> = ({ status, srid, attachments }) => {
  const { t } = useTranslation('project');
  const { batchId } = useParams();
  const client = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (variables: {
      srid: number;
      type: ProcessJobType;
      dxfOutputVersion?: string;
      minZoom?: number;
      maxZoom?: number;
      tileSize?: number;
      pointCloudAttributes?: string[];
      compressImages?: boolean;
      generateRcs?: boolean;
      rasterMode?: string;
    }) => {
      await execute(startBatchProcessing, {
        id: batchId!,
        srid: Number(variables.srid),
        type: variables.type,
        dxfOutputVersion: variables.dxfOutputVersion,
        minZoom: variables.minZoom,
        maxZoom: variables.maxZoom,
        tileSize: variables.tileSize,
        pointCloudAttributes: variables.pointCloudAttributes,
        compressImages: variables.compressImages,
        generateRcs: variables.generateRcs,
        rasterMode: variables.rasterMode,
      });

      return true;
    },

    onMutate: async () => {
      await client.cancelQueries({ queryKey: ['batch', batchId] });

      const previousBatch = client.getQueryData(['batch', batchId]);

      const newBatch = JSON.parse(JSON.stringify(previousBatch));
      if (newBatch?.data?.batch) {
        newBatch.data.batch.status = 'PROCESSING';
      }

      client.setQueryData(['batch', batchId], newBatch);

      return { previousBatch, newBatch };
    },
    onError: (_, __, context) => {
      client.setQueryData(['batch', batchId], context?.previousBatch);
    },
    onSettled: () => {
      client.invalidateQueries({ queryKey: ['batch', batchId] });
    },
  });
  const formik = useFormik({
    initialValues: {
      status: status,
      srid: srid,
      processType: getTypesFromFiles(attachments)[0] as ProcessJobType,
      dxfOutputVersion: dxfOutputVersions.ACAD2007,
      pointCloudAttributes: ['rgb', 'intensity'] as string[],
      compressImages: false,
      generateRcs: false,
      minZoom: 12,
      maxZoom: 20,
      tileSize: 256,
      rasterMode: 'rgb',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (values.processType === 'GEOTIFF') {
        if (values.minZoom < 0 || values.minZoom > 20) {
          errors.minZoom = t('batch.validationMinZoom');
        }
        if (values.maxZoom < 0 || values.maxZoom > 23) {
          errors.maxZoom = t('batch.validationMaxZoom');
        }
        if (values.minZoom >= values.maxZoom) {
          errors.minZoom = t('batch.validationMinMaxZoom');
        }
      }
      if (!values.processType) {
        errors.processType = t('batch.validationProcessType');
      }
      return errors;
    },
    onSubmit: async (values) => {
      const toastId = toast.loading(t('batch.startingProcessing'));
      try {
        await mutation.mutateAsync({
          srid: Number(values.srid || srid),
          type: values.processType,
          dxfOutputVersion: values.dxfOutputVersion,
          pointCloudAttributes: ['LAS', 'E57', 'PTS', 'RCS'].includes(values.processType)
            ? values.pointCloudAttributes
            : undefined,
          minZoom: values.processType === 'GEOTIFF' ? values.minZoom : undefined,
          maxZoom: values.processType === 'GEOTIFF' ? values.maxZoom : undefined,
          tileSize: values.processType === 'GEOTIFF' ? values.tileSize : undefined,
          rasterMode: values.processType === 'GEOTIFF' ? values.rasterMode : undefined,
          compressImages: values.compressImages,
          generateRcs: values.generateRcs,
        });
        toast.success(t('batch.processingStarted'), { id: toastId });
      } catch (e) {
        console.error(e);
        toast.error(t('batch.failedStartProcessing'), { id: toastId });
      }
    },
  });

  const processTypeList = useMemo(
    () => Object.keys(processTypes).map((key) => ({ key, label: t(`batch.processTypes.${key}`) })),
    [t],
  );

  if (status === 'WAITING') {
    return (
      <>
        <div className='flex flex-col gap-2 items-center justify-center'>
          <div className='[&>svg]:size-10 flex items-center justify-center'>{batchStatusMap.WAITING.icon}</div>
          <span className='text-sm text-slate-500 dark:text-stone-400 font-medium text-left'>
            {t('batch.readyForProcessing')}
          </span>
        </div>
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8 mb-4'>
          <div className='flex items-start gap-1 flex-col'>
            <Label>{t('batch.srid')}</Label>
            <SridSelector
              className='w-full'
              value={formik.values.srid.toString()}
              onChange={(value) => formik.setFieldValue('srid', parseInt(value))}
            />
          </div>
          <div className='flex items-start gap-1 flex-col'>
            <Label>{t('batch.processType')}</Label>
            <Select value={formik.values.processType} onValueChange={formik.handleChange('processType')}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder={t('batch.selectProcessType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {processTypeList.map(({ key, label }) => (
                    <SelectItem
                      key={key}
                      value={key}
                      disabled={getTypesFromFiles(attachments).includes(key) ? false : true}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {formik.values.processType === 'DWG' ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.outputVersion')}</Label>
              <Select value={formik.values.dxfOutputVersion} onValueChange={formik.handleChange('dxfOutputVersion')}>
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder={t('batch.selectOutputVersion')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {Object.entries(dxfOutputVersions).map(([key, value]) => (
                      <SelectItem key={key} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {formik.values.processType === 'GEOTIFF' ? (
            <>
              <div className='flex items-start gap-1 flex-col'>
                <Label>
                  {t('batch.minZoom')} ~1:{niceRound(mapboxScale(formik.values.minZoom)).toLocaleString()}
                </Label>
                <Input
                  type='number'
                  min={0}
                  max={25}
                  value={formik.values.minZoom}
                  onChange={(e) => formik.setFieldValue('minZoom', parseInt(e.target.value) || 0)}
                />
                {formik.errors.minZoom && <span className='text-xs text-red-500'>{formik.errors.minZoom}</span>}
              </div>
              <div className='flex items-start gap-1 flex-col'>
                <Label>
                  {t('batch.maxZoom')} ~1:{niceRound(mapboxScale(formik.values.maxZoom)).toLocaleString()}
                </Label>
                <Input
                  type='number'
                  min={0}
                  max={23}
                  value={formik.values.maxZoom}
                  onChange={(e) => formik.setFieldValue('maxZoom', parseInt(e.target.value) || 0)}
                />
                {formik.errors.maxZoom && <span className='text-xs text-red-500'>{formik.errors.maxZoom}</span>}
              </div>
              <div className='flex items-start gap-1 flex-col'>
                <Label>{t('batch.tileSize')}</Label>
                <Select
                  value={formik.values.tileSize.toString()}
                  onValueChange={(value) => formik.setFieldValue('tileSize', parseInt(value))}>
                  <SelectTrigger className='w-full'>
                    <SelectValue placeholder={t('batch.selectTileSize')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value='256'>256px</SelectItem>
                      <SelectItem value='512'>512px</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              {attachments.some((a) => /\.(las|laz)$/i.test(a.filename)) ? (
                <div className='flex items-start gap-1 flex-col'>
                  <Label>{t('batch.rasterMode')}</Label>
                  <Select
                    value={formik.values.rasterMode}
                    onValueChange={(value) => formik.setFieldValue('rasterMode', value)}>
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder={t('batch.selectRasterMode')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value='rgb'>RGB</SelectItem>
                        <SelectItem value='intensity'>{t('batch.rasterModeIntensity')}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </>
          ) : null}
          {['LAS', 'E57', 'PTS', 'RCS'].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.pointCloudAttributes')}</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' className='w-full justify-between'>
                    {formik.values.pointCloudAttributes.length > 0
                      ? t('batch.selected', { count: formik.values.pointCloudAttributes.length })
                      : t('batch.selectAttributes')}
                    <ChevronDownIcon className='h-4 w-4 opacity-50' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='w-56'>
                  {Object.entries(pointCloudAttributes).map(([key, value]) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={formik.values.pointCloudAttributes.includes(key)}
                      disabled={!['rgb', 'intensity'].includes(key) && formik.values.processType === 'E57'}
                      onSelect={(event) => {
                        event.preventDefault();
                      }}
                      onCheckedChange={(checked) => {
                        const currentAttributes = formik.values.pointCloudAttributes;
                        if (checked) {
                          formik.setFieldValue('pointCloudAttributes', [...currentAttributes, key]);
                        } else {
                          formik.setFieldValue(
                            'pointCloudAttributes',
                            currentAttributes.filter((attr) => attr !== key),
                          );
                        }
                      }}>
                      {t(`batch.pcAttributes.${pcAttributeKeys[key]}`)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
          {['E57', 'FJD_IMAGE'].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.compressImages')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={formik.values.compressImages}
                  onCheckedChange={(checked) => formik.setFieldValue('compressImages', checked)}
                />
              </div>
            </div>
          ) : null}
          {['LAS', 'PTS', 'E57'].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.generateRcs')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={formik.values.generateRcs}
                  onCheckedChange={(checked) => formik.setFieldValue('generateRcs', checked)}
                />
              </div>
            </div>
          ) : null}
          <div className='flex items-start gap-1 flex-col justify-end'>
            {/* <Label>Output Version</Label> */}
            <Button type='submit' className='w-full' onClick={formik.submitForm} disabled={formik.isSubmitting}>
              {t('batch.process')}
            </Button>
          </div>
        </div>
      </>
    );
  } else if (status === 'PROCESSING') {
    return (
      <div className='flex flex-col gap-2 items-center justify-center'>
        <div className='[&>svg]:size-10 flex items-center justify-center'>{batchStatusMap.PROCESSING.icon}</div>
        <span className='text-sm text-slate-500 font-medium text-left'>{t('batch.processing')}</span>
      </div>
    );
  } else if (status === 'FAILED') {
    return (
      <div className='flex flex-col gap-2 items-center justify-center'>
        <div className='[&>svg]:size-10 flex items-center justify-center'>{batchStatusMap.FAILED.icon}</div>
        <span className='text-sm text-slate-500 font-medium text-left'>{t('batch.failedToProcess')}</span>
      </div>
    );
  }
  return null;
};

export default Header;
